import { call, delay, fork, put, race, select, spawn, take, takeLatest } from 'redux-saga/effects';

/* Project */
import { setErrorMessage } from 'content/app.actions';
import apiRequest, { apiSuccess, controlledCall, getDocumentApi } from 'utils/api';
import defaultErrorToast from 'utils/toastify/defaultErrorToast';
import { getSocketId } from 'utils/socketId';
import {
  controlsChanged,
  getAvailabilities,
  getDates,
  getDatesSuccess,
  getDocument,
  getDocumentFail,
  getDocumentSuccess,
  getTechniciansAndAvailabilities,
  getUserData,
  getUserVehicle,
  getUserVehicleSuccess,
  getUserVehicleWithConectaApi,
  getUserVehicleWithConectaApiFail,
  getUserVehicleWithConectaApiSuccess,
  onChangeImage,
  resetIsFindedVehicle,
  setControlsErrors,
  submitReSchedule,
  submitSchedule,
  toggleSchedulerError,
} from './schedulerSteps.actions';
import SchedulerStepsTypes from './schedulerSteps.types';
import isBrandSelectable from './data/brandSelection';

function* resetIsFindedVehicleGenerator() {
  const isFindedVehicle = yield select((store) => store.schedulerSteps.isFindedVehicle);

  if (isFindedVehicle) {
    yield put(resetIsFindedVehicle({ isFindedVehicle: !isFindedVehicle }));
  }
}

function* getScheduleVoucherDocument() {
  yield takeLatest(getDocument, function* getScheduleVoucher(action) {
    /* const response = yield api('api/v1/scheduler/voucher', { method: 'get' }); */

    const successFunction = (payload) => ({
      type: SchedulerStepsTypes.GET_VOUCHER_DOCUMENT_SUCCESS,
      payload,
    });

    const failFunction = (payload) => ({
      type: SchedulerStepsTypes.GET_VOUCHER_DOCUMENT_FAIL,
      payload,
    });

    const { type, ticketId, newDate, oldDate, offset } = action.payload;

    let title;
    if (type === 'schedule') {
      title = 'TicketAgendamiento';
    } else if (type === 'reschedule') {
      title = 'TicketReAgendamiento';
    } else {
      title = 'TicketAnulacion';
    }

    const response = yield controlledCall(
      getDocumentApi(`${title}-${ticketId}`, 'pdf', true),
      `api/v1/scheduler/voucher?type=${type}&ticketId=${ticketId}&newDate=${newDate}&oldDate=${oldDate}&offset=${offset}`,
      null,
      successFunction,
      failFunction,
    );

    if (!response.error) {
      yield put(getDocumentSuccess());
    } else {
      yield put(getDocumentFail());
      defaultErrorToast({ message: response.message });
    }
  });
}

// agregar variable de entorno para cambiar endpoit en el cual se llama la integración
function* getUserVehicleData() {
  yield takeLatest(getUserVehicle, function* getUserVehicleDataFunc(action) {
    const { patent } = action.payload;

    const response = yield apiRequest(`api/v1/scheduler/uservehicle/${patent}`, { method: 'get' });

    if (response.error) {
      const { errorMsg } = response;
      yield resetIsFindedVehicleGenerator();

      return yield put(toggleSchedulerError({ errorMsg }));
    }

    const { userVehicle, client, contacts } = response;

    return yield put(getUserVehicleSuccess({ userVehicle, client, contacts }));
  });
}

/* The answer arrives through the socket ([VEHICLE_QUEUE_RESPONSE], App.jsx). The vehicle
   fields stay blocked while waiting, so a lost answer must not keep them blocked: Conecta
   and SAP usually answer in 2 to 5 seconds. */
const VEHICLE_SEARCH_TIMEOUT_MS = 15000;

/* fetchToUrl answers undefined when the request did not reach the API. */
const vehicleSearchRequestFailed = (response) => !response || !!response.error;

const isSuccessFor = (patent, action) =>
  action.type === getUserVehicleWithConectaApiSuccess.type && action.payload.patent === patent;

/* A failure also ends the search; a late answer for another plate is ignored. */
const isAnswerFor = (patent) => (action) =>
  action.type === getUserVehicleWithConectaApiFail.type || isSuccessFor(patent, action);

const orEmpty = (value) => value ?? '';

const NO_VEHICLE_SELECTION = {
  brand: { id: '', name: '', externalId: '' },
  modelId: '',
  versionId: '',
  year: '',
};

/* Only brands the user could have picked in the brand selector are preselected. */
const findSelectableBrand = (brands, brandId) =>
  [brands[brandId]].filter(Boolean).find(isBrandSelectable);

/* The brand, model and version matched from SAP, down to the last level that matched, and
   the year SAP knows. Without a selectable brand nothing is filled in, not even the year. */
const vehicleSelection = (brands, userVehicle) => {
  const brand = findSelectableBrand(brands, userVehicle.brandId);

  return brand
    ? {
        brand,
        modelId: orEmpty(userVehicle.modelId),
        versionId: orEmpty(userVehicle.versionId),
        year: orEmpty(userVehicle.year),
      }
    : NO_VEHICLE_SELECTION;
};

/* Failed searches carry no payload and therefore no vehicle. */
const answeredVehicle = ({ payload }) => (payload ? payload.userVehicle : {});

/* Each plate search decides the vehicle selection, replacing whatever was picked before: the
   levels matched from SAP and the year are filled in and stay editable, and a search without
   data clears the brand, model, version and year. */
function* applyVehicleSearchAnswer(answer) {
  const brands = yield select((store) => store.app.objects.brands);
  const { brand, modelId, versionId, year } = vehicleSelection(brands, answeredVehicle(answer));

  yield put(onChangeImage(brand));
  yield put(controlsChanged({ name: 'modelId', value: modelId }));
  yield put(controlsChanged({ name: 'versionId', value: versionId }));
  yield put(controlsChanged({ name: 'year', value: year }));
  yield put(
    setControlsErrors({ controlsErrors: { brandId: '', modelId: '', versionId: '', year: '' } }),
  );
}

/* The failure put here is taken as the search answer. */
function* failVehicleSearchAfterTimeout() {
  yield delay(VEHICLE_SEARCH_TIMEOUT_MS);
  yield put(getUserVehicleWithConectaApiFail());
}

function* waitForVehicleSearchAnswer(patent) {
  const { answer } = yield race({
    answer: take(isAnswerFor(patent)),
    timeout: call(failVehicleSearchAfterTimeout),
  });

  yield call(applyVehicleSearchAnswer, answer);
}

function* failVehicleSearchIfRequestFailed(response) {
  if (vehicleSearchRequestFailed(response)) yield put(getUserVehicleWithConectaApiFail());
}

/* The socket answer can arrive before the HTTP response, so the wait starts first. Whatever
   goes wrong only means nothing is autocompleted: no error is shown, but the vehicle fields
   are unblocked. */
function* searchVehicleByPatent(action) {
  try {
    const { patent } = action.payload;

    yield fork(waitForVehicleSearchAnswer, patent);
    const response = yield apiRequest(
      `api/v1/scheduler/search_by_license_plate?patent=${patent}&socketId=${getSocketId()}`,
      { method: 'get' },
    );

    yield call(failVehicleSearchIfRequestFailed, response);
  } catch (error) {
    yield put(getUserVehicleWithConectaApiFail());
  }
}

// ### socket logic
function* getUserVehicleDataWithConectaApiSaga() {
  yield takeLatest(getUserVehicleWithConectaApi, searchVehicleByPatent);
}

function* getUserInfo() {
  yield takeLatest(getUserData, function* getUserDataFunc(action) {
    const { rut } = action.payload;
    const socketId = getSocketId();
    const response = yield apiRequest(
      `api/v1/scheduler/user_by_rut?rut=${rut}&socketId=${socketId}`,
      { method: 'get' },
    );

    if (response.error) {
      return yield put(
        apiSuccess(setErrorMessage, { msg: 'Falló la carga inicial de información.' }),
      );
    }
    return response;
  });
}

function* getDatesData() {
  yield takeLatest(getDates, function* getDatesFunc() {
    const response = yield apiRequest('api/v1/scheduler/dates', { method: 'get' });

    if (response.error) {
      return yield put(apiSuccess(setErrorMessage, { msg: 'Falló la carga.' }));
    }

    return yield put(getDatesSuccess(response));
  });
}

// TODO: end logic of this saga
function* submitScheduleData() {
  yield takeLatest(submitSchedule, function* getDatesFunc(action) {
    // const isFindedVehicle = yield select((store) => store.schedulerSteps.isFindedVehicle);
    const socketId = getSocketId();

    const { controls, selectedService, addContact } = action.payload;
    const payloadData = { controls, selectedService, addContact, socketId };
    const body = JSON.stringify(payloadData);
    yield apiRequest('api/v1/scheduler/submit', { method: 'post', body });
    // console.log({
    //   response,
    // });

    // if (!response.error) {
    //   navigate('/schedule/success');
    // } else {
    //   return yield put(apiSuccess(setErrorMessage, { msg: 'Falló la carga.' }));
    // }

    // return yield put(submitScheduleSuccess(response));
  });
}
function* submitReScheduleData() {
  yield takeLatest(submitReSchedule, function* submitRescheduleFunc(action) {
    const socketId = getSocketId();

    const payloadData = { ...action.payload, socketId };
    const body = JSON.stringify(payloadData);
    yield apiRequest('api/v1/scheduler/reschedule/submit', {
      method: 'post',
      body,
    });
    // console.log({
    //   response,
    // });
  });
}

/* function* submitReScheduleData() {
  yield takeLatest(submitReSchedule, function* submitRescheduler(action) {
    const { controls, navigate } = action.payload;
    const payloadData = { controls };
    const body = JSON.stringify(payloadData);
    const response = yield apiRequest('api/v1/rescheduler/submit', { method: 'post', body });

    if (!response.error) {
      navigate('/reschedule/success');
    } else {
      return yield put(apiSuccess(setErrorMessage, { msg: 'Falló la carga.' }));
    }

    return yield put(submitReScheduleSuccess(response));
  });
} */

function* getTechniciansAndAvailabilitiesData() {
  yield takeLatest(
    getTechniciansAndAvailabilities,
    function* getTechniciansAndAvailabilitiesFunc(action) {
      const { selectedService, workshopId, modelId, kmId, diagnosisType, startDate, endDate } =
        action.payload;
      const socketId = getSocketId();
      const response = yield apiRequest(
        `api/v1/scheduler/availabilities_and_technicians?selectedService=${selectedService}&socketId=${socketId}&workshopId=${workshopId}&modelId=${modelId}&kmId=${kmId}&diagnosisType=${diagnosisType}&startDate=${startDate}&endDate=${endDate}`,
        { method: 'get' },
      );

      if (response.error) {
        return yield put(
          apiSuccess(setErrorMessage, { msg: 'Falló la carga inicial de información.' }),
        );
      }

      return response;
    },
  );
}

function* getAvailabilitiesData() {
  yield takeLatest(getAvailabilities, function* getAvailabilitiesFunc(action) {
    const { selectedService, startDate, endDate, technicianBusinessPartnerId } = action.payload;
    const socketId = getSocketId();
    const response = yield apiRequest(
      `api/v1/scheduler/technician_availabilities?socketId=${socketId}&selectedService=${selectedService}&startDate=${startDate}&endDate=${endDate}&technicianBusinessPartnerId=${technicianBusinessPartnerId}`,
      { method: 'get' },
    );

    if (response.error) {
      return yield put(toggleSchedulerError({ errorMsg: response.error }));
    }

    return response;
  });
}

export default function* SchedulerSaga() {
  yield spawn(getScheduleVoucherDocument);
  yield spawn(getUserVehicleData);
  yield spawn(getUserInfo);
  yield spawn(getDatesData);
  yield spawn(submitScheduleData);
  yield spawn(getUserVehicleDataWithConectaApiSaga);
  yield spawn(getTechniciansAndAvailabilitiesData);
  yield spawn(getAvailabilitiesData);
  yield spawn(submitReScheduleData);
}
